import {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "src/utils/AuthContext";
import {
  Box,
  Button,
  Typography,
  IconButton,
  MenuList,
  MenuItem,
  ListItemIcon,
  Icon,
  Collapse,
  Divider,
  Drawer,
  Link,
  Switch,
  Chip,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import {
  BookOutlined,
  InfoOutlined,
  ExpandLess,
  ExpandMore,
  RocketLaunchOutlined,
  WorkspacePremiumOutlined,
  LoginOutlined,
  LogoutOutlined,
  BugReportOutlined,
  DeviceHubOutlined,
  LightModeOutlined,
  UpgradeOutlined,
  HelpOutlineOutlined,
  SupportOutlined,
  CloseOutlined,
  AddCircle,
  WavingHandOutlined,
  CreditCardOutlined,
} from "@mui/icons-material";
import { handleCustomerPortal } from "src/utils/Utils";
import {
  canManageSubscription,
  Features,
  getPageRoute,
  getUpgradeFlow,
  hasAccess,
  isFreeUser,
  needsSomeUpgrade,
} from "src/services";
import { PlanComparisonTable } from "src/components/upgrades/PlanComparisonTable.js";
import { customEvent } from "src/utils/gtag.js";
import CalendarExplainerTile from "src/components/explainers/CalendarExplainerTile.js";
import {
  GOOGLE_CONNECT_STATES,
  googleConnectHealthLine,
  googleConnectState,
  isGoogleConnectStopped,
  isGoogleDisconnectPending,
  showsGoogleConnect,
  turnOffGoogleConnectState,
} from "src/utils/googleConnect.js";
import {
  RECONNECT_STATE,
  startGoogleConnectOauth,
} from "src/utils/googleConnectOauth.js";
import { useGoogleCalendarStatus } from "src/services/queries/useGoogleCalendarStatus.js";
import { isCalendarPersonalizationEnabled } from "src/services/Config.js";
import { useGoogleDisconnectRefresh } from "src/utils/useGoogleDisconnectRefresh.js";
import { useModalScrollBlocker } from "src/utils/useModalScrollBlocker.js";
import { UAParser } from "ua-parser-js";
import { Portal } from "src/utils/Portal.js";

const GOOGLE_CONNECT_CHIPS = {
  [GOOGLE_CONNECT_STATES.BROKEN]: { label: "Stopped", color: "error" },
  [GOOGLE_CONNECT_STATES.INACTIVE]: { label: "Stopped", color: "error" },
  [GOOGLE_CONNECT_STATES.OFF]: { label: "Recommended", color: "primary" },
};
const GOOGLE_CONNECT_PENDING_CHIP = { label: "Turning off", color: "default" };
// Width of the row's icon slot, so the CTA below starts where the row text does.
const GC_TEXT_INSET = "36px";
// Lives in a nested box because the MenuList's `&>*` rule force-zeroes padding
// on its direct children and would otherwise out-specify this inset.
const GC_CTA_SX = {
  pl: GC_TEXT_INSET,
  pb: 1,
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 1,
};

const Check = () => (
  <Icon
    baseClassName="material-symbols-outlined"
    sx={{ fontWeight: 300, fontSize: "1rem" }}
  >
    check
  </Icon>
);

export const SideMenu = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleSignOut } = useContext(AuthContext);
  const [openUpgrade, setOpenUpgrade] = useState(false);
  const [openBlog, setOpenBlog] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const lastScroll = useRef(0);
  const uaParser = new UAParser();
  const parserResults = uaParser.getResult();

  const loggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  const handleSignOutClick = async () => {
    await handleSignOut();
    navigate(getPageRoute("auth", "SIGNIN"));
  };

  const gcState = googleConnectState(user);
  const gcDisconnectPending = isGoogleDisconnectPending(user);
  const gcChip = gcDisconnectPending
    ? GOOGLE_CONNECT_PENDING_CHIP
    : GOOGLE_CONNECT_CHIPS[gcState];
  useGoogleDisconnectRefresh(open);

  const { data: googleCalendarStatus } = useGoogleCalendarStatus(
    { user },
    { enabled: open && gcState === GOOGLE_CONNECT_STATES.ON }
  );

  // `lastUpdated` is stamped when a sync starts, so only a successful row can
  // stand for "last updated" — a failed one would read as healthy.
  const lastSyncedAt = useMemo(() => {
    const stamps = (googleCalendarStatus?.calendars || [])
      .filter((row) => row.status === "success")
      .map((row) => row.lastUpdated)
      .filter(Boolean);
    if (!stamps.length) return null;
    return new Date(Math.max(...stamps.map((stamp) => new Date(stamp).getTime()))).toISOString();
  }, [googleCalendarStatus]);

  const trackGoogleConnect = (name, label) => {
    customEvent({
      name,
      category: "GoogleConnect",
      label,
      data: {
        user_id: user?._id,
        user_email: user?.email,
        user_handle: user?.handle ?? "no_handle",
        connection_state: gcState,
      },
    });
  };

  const handleConnectGoogleCalendar = () => {
    trackGoogleConnect(
      "user_click_google_connect_toggle_on",
      "Google Connect Toggle On"
    );
    if (hasAccess(user, Features.CONNECT_GOOGLE_CALENDAR.key)) {
      navigate(getPageRoute("help", "CONNECT_GOOGLE_CALENDAR"));
    } else {
      navigate(
        getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR_STEPS_STANDALONE")
      );
    }
    onClose();
  };

  const handleReconnectGoogleCalendar = () => {
    trackGoogleConnect("google_connect_row_reconnect_clicked", "Reconnect");
    startGoogleConnectOauth(RECONNECT_STATE);
  };

  const handleResubscribeStanzaPlus = () => {
    trackGoogleConnect("google_connect_row_resubscribe_clicked", "Resubscribe");
    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
      state: { source: "google_connect_row" },
    });
    onClose();
  };

  const handleOffboardGoogleCalendar = () => {
    trackGoogleConnect(
      "google_connect_offboarding_opened",
      "Google Connect offboarding opened"
    );
    navigate(getPageRoute("help", "GOOGLE_CONNECT_OFF"), {
      state: turnOffGoogleConnectState(location),
    });
    onClose();
  };

  const handleGoogleConnectRowClick = () => {
    // While the removal runs, the connect page is the one surface that explains
    // the wait and offers turning sync back on once it finishes.
    if (gcDisconnectPending) {
      trackGoogleConnect(
        "google_connect_row_pending_opened",
        "Google Connect pending opened"
      );
      navigate(getPageRoute("help", "CONNECT_GOOGLE_CALENDAR"));
      onClose();
      return;
    }
    if (gcState === GOOGLE_CONNECT_STATES.OFF) return handleConnectGoogleCalendar();
    if (gcState === GOOGLE_CONNECT_STATES.BROKEN)
      return handleReconnectGoogleCalendar();
    if (gcState === GOOGLE_CONNECT_STATES.ON)
      return handleOffboardGoogleCalendar();
    return handleResubscribeStanzaPlus();
  };

  const commonItems = {
    about: () => (
      <MenuItem
        component={RouterLink}
        to="https://stanza-1.gitbook.io/stanza-help-center/stanza-overview/quickstart"
        target="_blank"
        onClick={onClose}
      >
        <ListItemIcon>
          <InfoOutlined
            fontSize="medium"
            sx={{ color: `${theme.palette.icon.primary}` }}
          />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          About
        </Typography>
      </MenuItem>
    ),
    vision: () => (
      <MenuItem component={RouterLink} to="/vision" onClick={onClose}>
        <ListItemIcon>
          <Icon
            baseClassName="material-symbols-outlined"
            fontSize="medium"
            sx={{ color: `${theme.palette.icon.primary}` }}
          >
            volunteer_activism
          </Icon>
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Giving Back
        </Typography>
      </MenuItem>
    ),
    upgrade: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenUpgrade(!openUpgrade)}
        key="upgrade-menu-item"
      >
        <ListItemIcon>
          <UpgradeOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Upgrade
        </Typography>
        {openUpgrade ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse
        in={openUpgrade}
        timeout="auto"
        unmountOnExit
        key="upgrade-collapse"
      >
        <Box>
          {user && !user.hasValidSubscription && (
            <MenuItem
              onClick={() => {
                onClose();
                navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                  state: { source: "nav_dropdown" },
                });
              }}
            >
              <ListItemIcon>
                <RocketLaunchOutlined
                  sx={{ color: `${theme.palette.icon.primary}` }}
                />
              </ListItemIcon>
              <Typography variant="h5" textAlign="center">
                Stanza+
              </Typography>
            </MenuItem>
          )}
        </Box>
      </Collapse>,
    ],
    support: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenSupport(!openSupport)}
        key="support-menu-item"
      >
        <ListItemIcon>
          <SupportOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Support
        </Typography>
        {openSupport ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse
        in={openSupport}
        timeout="auto"
        unmountOnExit
        key="support-collapse"
      >
        <Box>
          <MenuItem
            component={RouterLink}
            to="https://stanza-1.gitbook.io/stanza-help-center"
            target="_blank"
            data-testid="home-user-menu-help-center"
          >
            <ListItemIcon>
              <HelpOutlineOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Help Center
            </Typography>
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to={`https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform`}
            target="_blank"
            data-testid="home-user-menu-report-issue"
          >
            <ListItemIcon>
              <BugReportOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Report
            </Typography>
          </MenuItem>
        </Box>
      </Collapse>,
    ],
    blog: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenBlog(!openBlog)}
        key="blog-menu-item"
      >
        <ListItemIcon>
          <BookOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Blog
        </Typography>
        {openBlog ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse in={openBlog} timeout="auto" unmountOnExit key="blog-collapse">
        <Box>
          <MenuItem
            component={RouterLink}
            to="https://stanzacal.ghost.io"
            target="_blank"
            onClick={onClose}
          >
            <ListItemIcon>
              <LightModeOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Spotlight
            </Typography>
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to="https://creatorhub.stanzacal.com"
            target="_blank"
            onClick={onClose}
            data-testid="home-user-creator-hub"
          >
            <ListItemIcon>
              <DeviceHubOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Creator Hub
            </Typography>
          </MenuItem>
        </Box>
      </Collapse>,
    ],
  };

  const menuListSx = {
    "&>*": {
      px: "0px !important",
    },
    "&>hr": {
      my: 1,
    },
    "& .MuiCollapse-root": {
      mt: 0.5,
    },
    "&>:hover, & li:hover, & a:hover": {
      borderRadius: "4px",
    },
    "&>a:first-child, &>li:first-child": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>a:not(:first-child), &>li:not(:first-child)": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>div a:last-child, &>div li:last-child": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>div a:not(:last-child), &>div li:not(:last-child)": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
  };

  const renderMenu = () =>
    loggedIn ? (
      <MenuList sx={menuListSx}>
        {showsGoogleConnect(user) && (
          <>
            <MenuItem
              data-testid="menu-google-connect"
              data-google-connect-state={gcState}
              data-google-disconnect-pending={gcDisconnectPending}
              onClick={handleGoogleConnectRowClick}
            >
              <ListItemIcon sx={{ minWidth: GC_TEXT_INSET }}>
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="medium"
                  sx={{
                    color:
                      !gcDisconnectPending && isGoogleConnectStopped(user)
                        ? theme.palette.error.main
                        : theme.palette.icon.primary,
                  }}
                >
                  sync
                </Icon>
              </ListItemIcon>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h5">Google Connect</Typography>
                  {gcChip && (
                    <Chip
                      label={gcChip.label}
                      color={gcChip.color}
                      variant="outlined"
                      size="small"
                      sx={{
                        height: "24px",
                        fontSize: "11px",
                        fontWeight: 500,
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: "11px",
                        },
                        // Pulse while the removal runs so the row reads as active work.
                        ...(gcDisconnectPending && {
                          "@keyframes gcTurningOff": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.35 },
                          },
                          animation: "gcTurningOff 1.4s ease-in-out infinite",
                          "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                          },
                        }),
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ whiteSpace: "normal" }}
                >
                  {googleConnectHealthLine(gcState, {
                    expiredAt: user?.googleCalendar?.expiredAt,
                    lastUpdated: lastSyncedAt,
                    disconnectPending: gcDisconnectPending,
                  })}
                </Typography>
              </Box>
              <Switch
                checked={gcState !== GOOGLE_CONNECT_STATES.OFF}
                disabled={gcDisconnectPending}
                color={
                  gcState === GOOGLE_CONNECT_STATES.BROKEN ? "error" : "primary"
                }
                sx={{
                  ml: "auto",
                  opacity:
                    gcState === GOOGLE_CONNECT_STATES.INACTIVE ? 0.5 : 1,
                }}
                size="small"
              />
            </MenuItem>
            {gcState === GOOGLE_CONNECT_STATES.BROKEN && (
              <Box>
                <Box sx={GC_CTA_SX}>
                  <Button
                    data-testid="button-google-connect-reconnect"
                    variant="contained"
                    size="shrinked"
                    onClick={handleReconnectGoogleCalendar}
                  >
                    RECONNECT
                  </Button>
                </Box>
              </Box>
            )}
            {gcState === GOOGLE_CONNECT_STATES.INACTIVE && (
              <Box>
                <Box sx={GC_CTA_SX}>
                  <Button
                    data-testid="button-google-connect-resubscribe"
                    variant="contained"
                    size="shrinked"
                    onClick={handleResubscribeStanzaPlus}
                  >
                    UPGRADE
                  </Button>
                  <Button
                    data-testid="button-google-connect-offboard"
                    variant="outlined"
                    size="shrinked"
                    onClick={handleOffboardGoogleCalendar}
                  >
                    TURN OFF
                  </Button>
                </Box>
              </Box>
            )}
            <Divider />
          </>
        )}
        {user.handle &&
          [...(user.createdCalendars || []), ...(user.createdGroups || [])]
            .length > 0 && (
            <>
              <MenuItem
                component={RouterLink}
                to={`/${user.handle}`}
                onClick={onClose}
              >
                <ListItemIcon>
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  >
                    location_home
                  </Icon>
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Profile
                </Typography>
              </MenuItem>
              <Divider />
            </>
          )}
        {user.hasFailedSubscriptionPayment && (
          <>
            <MenuItem
              data-testid="side-menu-update-payment"
              onClick={() => {
                onClose();
                navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                  state: { source: "side_menu_update_payment" },
                });
              }}
            >
              <ListItemIcon>
                <CreditCardOutlined
                  sx={{ color: `${theme.palette.error.main}` }}
                />
              </ListItemIcon>
              <Typography
                variant="h5"
                textAlign="center"
                sx={{ color: `${theme.palette.error.main}` }}
              >
                Update payment method
              </Typography>
            </MenuItem>
            <Divider />
          </>
        )}
        {canManageSubscription(user) &&
          (user?.lifetimeMember ? (
            <>
              <MenuItem
                onClick={() => {
                  onClose();
                  navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                    state: { source: "side_menu_your_plan" },
                  });
                }}
              >
                <ListItemIcon>
                  <WorkspacePremiumOutlined
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  />
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Plan
                </Typography>
              </MenuItem>
              <Divider />
            </>
          ) : (
            <>
              <MenuItem
                data-testid="side-menu-your-plan"
                onClick={handleCustomerPortal}
              >
                <ListItemIcon>
                  <WorkspacePremiumOutlined
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  />
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Plan
                </Typography>
              </MenuItem>
              <Divider />
            </>
          ))}
        {commonItems.about()}
        <Divider />
        {commonItems.vision()}
        <Divider />
        {needsSomeUpgrade(user) && [
          ...commonItems.upgrade(),
          <Divider key="upgrade-divider" />,
        ]}
        {commonItems.blog()}
        <Divider />
        {commonItems.support()}
        <Divider />
        <MenuItem
          component={RouterLink}
          to={getPageRoute("user", "EMAIL_PREFERENCES")}
          onClick={onClose}
          data-testid="home-user-menu-email-preferences"
        >
          <ListItemIcon>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ color: `${theme.palette.icon.primary}` }}
            >
              mail
            </Icon>
          </ListItemIcon>
          <Typography variant="h5" textAlign="center">
            Email Preferences
          </Typography>
        </MenuItem>
        <Divider />
        {isCalendarPersonalizationEnabled() && [
          <MenuItem
            key="event-defaults"
            component={RouterLink}
            to={getPageRoute("user", "EVENT_DEFAULTS")}
            onClick={onClose}
            data-testid="home-user-menu-event-defaults"
          >
            <ListItemIcon>
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{ color: `${theme.palette.icon.primary}` }}
              >
                tune
              </Icon>
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Event Defaults
            </Typography>
          </MenuItem>,
          <Divider key="event-defaults-divider" />,
        ]}
        <MenuItem
          to="/signin"
          onClick={handleSignOutClick}
          data-testid="home-user-menu-sign-out"
        >
          <ListItemIcon>
            <LogoutOutlined color="delete" />
          </ListItemIcon>
          <Typography variant="h5" textAlign="center" color="delete.main">
            Sign Out
          </Typography>
        </MenuItem>
      </MenuList>
    ) : (
      <MenuList sx={menuListSx}>
        {commonItems.about()}
        <Divider />
        {commonItems.vision()}
        <Divider />
        {commonItems.blog()}
        <Divider />
        {commonItems.support()}
        <Divider />
        <MenuItem component={RouterLink} to="/signin" onClick={onClose}>
          <ListItemIcon>
            <LoginOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
          </ListItemIcon>
          <Typography variant="h5" textAlign="center">
            Sign In
          </Typography>
        </MenuItem>
      </MenuList>
    );

  const handleExploreCalendars = () => {
    navigate(getPageRoute("home", "PERSONALIZE"));
    onClose();
  };

  useModalScrollBlocker({ open });

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        container={document.getElementById("portal-root")}
        disableScrollLock={true}
        PaperProps={{
          className: "thin-scrollbar",
          sx: {
            width: "342px",
            p: 2,
            boxShadow: theme.shadows[16],
            overflowY: "auto",
            height: "100vh",
            "@supports (height: 100dvh)": {
              minHeight: "100dvh",
            },
            "@supports not (height: 100dvh)": {
              minHeight: "-webkit-fill-available",
            },
          },
        }}
        ModalProps={{
          BackdropProps: {
            sx: {
              height: "100vh",
              "@supports (height: 100dvh)": {
                height: "100dvh",
              },
              "@supports not (height: 100dvh)": {
                height: "-webkit-fill-available",
              },
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: "260px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {!loggedIn ? (
              <Link component={RouterLink} to={getPageRoute("auth", "SIGNIN")}>
                Sign into Account
              </Link>
            ) : (
              <Typography variant="body1">{user?.email}</Typography>
            )}
            <IconButton onClick={onClose}>
              <CloseOutlined fontSize="medium" />
            </IconButton>
          </Box>
          <Divider sx={{ mt: 1, mb: 4 }} />
          {!loggedIn ? (
            <>
              <Typography
                variant={"h2"}
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <WavingHandOutlined />
                Welcome to Stanza
              </Typography>
              <Typography variant={"body1"} sx={{ mb: 2 }}>
                Where community and calendars meet. Subscribe to what you love
                or create your own calendars for others to follow.
              </Typography>
              <Button
                variant="contained"
                component={RouterLink}
                to="/creators"
                onClick={onClose}
                fullWidth
                sx={{
                  backgroundColor: "black",
                  color: "white",
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                  },
                }}
              >
                Create Your Calendar
              </Button>
              <Divider sx={{ mt: 4 }} />
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant={"body2"} data-testid="side-menu-your-plan-label" sx={{ mb: 0.5 }}>
                    Your Plan
                  </Typography>
                  <Typography variant={"h2"} data-testid="side-menu-your-plan-value" sx={{ mb: 0.5 }}>
                    {user?.enterpriseCreator
                      ? "Stanza Enterprise"
                      : user?.hasValidCreatorSubscription
                      ? "Indie Creator"
                      : user?.hasValidSubscription &&
                        user?.hasValidCreatorSubscription
                      ? "Stanza+, Indie Cerator"
                      : user?.hasValidSubscription
                      ? "Stanza+"
                      : user?.hasValidCreatorSubscription
                      ? "Indie Creator"
                      : "Stanza Free"}
                  </Typography>
                </Box>
                {isFreeUser(user) && (
                  <Button
                    variant="contained"
                    size="shrinked"
                    sx={{ height: "min-content", alignSelf: "flex-end" }}
                    onClick={() => {
                      navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                        state: { source: "side_menu_upgrade_button" },
                      });
                      customEvent({
                        name: "user_click_side_menu_upgrade",
                        category: "Upgrade",
                        label: "SideMenu Upgrade Button (Logged In)",
                      });
                      onClose();
                    }}
                  >
                    UPGRADE
                  </Button>
                )}
              </Box>
              {!isFreeUser(user) && (
                <>
                  <Button
                    variant="outlined"
                    size="shrinked"
                    as="a"
                    href={
                      user?.enterpriseCreator ||
                      user?.hasValidCreatorSubscription
                        ? "https://creatorhub.stanzacal.com/general-questions-678321613027fd001b426f91/"
                        : "https://spotlight.stanzacal.com/for-stanza-plus-members/"
                    }
                    target="_blank"
                    onClick={() => {
                      onClose();
                    }}
                    sx={{
                      my: 2,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    See all benefits
                  </Button>
                </>
              )}
              <Divider sx={{ mt: 2 }} />
            </>
          )}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {renderMenu()}
            {!loggedIn && (
              <CalendarExplainerTile
                onButtonClick={handleExploreCalendars}
                buttonText="EXPLORE CALENDARS"
                location="side_menu"
                sx={{ mx: -2 }}
              />
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
